import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { API_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';

type Notif = {
    ticketId: string;
    messageId: string;
    subject: string;
    body: string;
    authorName: string;
    createdAt: string;
};

/** Affiche les réponses support admin en notification toast. */
const SupportNotifications: React.FC = () => {
    const { isAuthenticated, token, isAdmin } = useAuth();
    const [notifs, setNotifs] = useState<Notif[]>([]);
    const [open, setOpen] = useState<Notif | null>(null);

    const load = useCallback(async () => {
        if (!isAuthenticated || !token || isAdmin) return;
        try {
            const res = await fetch(`${API_URL}/support/notifications`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const data = await res.json();
            setNotifs(Array.isArray(data) ? data : []);
            if (Array.isArray(data) && data.length > 0 && !open) {
                setOpen(data[0]);
            }
        } catch (_) {
            /* ignore */
        }
    }, [isAuthenticated, token, isAdmin, open]);

    useEffect(() => {
        load();
        const t = window.setInterval(load, 45000);
        return () => clearInterval(t);
    }, [load]);

    const dismiss = async (n: Notif) => {
        try {
            await fetch(`${API_URL}/support/notifications/read`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ ticketId: n.ticketId, messageId: n.messageId }),
            });
        } catch (_) {
            /* ignore */
        }
        setNotifs((prev) => prev.filter((x) => x.messageId !== n.messageId));
        setOpen((cur) => (cur?.messageId === n.messageId ? null : cur));
    };

    if (!isAuthenticated || isAdmin) return null;

    return (
        <div className="no-print fixed bottom-24 right-5 z-[75] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
            {notifs.length > 0 && !open && (
                <button
                    type="button"
                    onClick={() => setOpen(notifs[0])}
                    className="pointer-events-auto self-end bg-indigo-600 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-bold"
                >
                    <Bell className="w-4 h-4" />
                    {notifs.length} réponse{notifs.length > 1 ? 's' : ''} support
                </button>
            )}
            {open && (
                <div className="pointer-events-auto bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 relative animate-fade-in">
                    <button
                        type="button"
                        className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
                        onClick={() => dismiss(open)}
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <p className="text-[10px] font-black uppercase tracking-wider text-indigo-600 mb-1">
                        Réponse support
                    </p>
                    <p className="font-bold text-slate-900 pr-6">{open.subject}</p>
                    <p className="text-xs text-slate-500 mt-0.5">de {open.authorName}</p>
                    <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">
                        {open.body}
                    </p>
                    <button
                        type="button"
                        onClick={() => dismiss(open)}
                        className="mt-4 w-full text-sm font-bold bg-slate-900 text-white rounded-xl py-2"
                    >
                        Compris
                    </button>
                </div>
            )}
        </div>
    );
};

export default SupportNotifications;
